import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DramaIcon, FolderSearchIcon, LayersIcon, PartyPopperIcon, ShuffleIcon, SparklesIcon, UsersIcon, ZapIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// =====================================================================
// TEATERKLAR™ – fuente única de textos de la landing (sueco, desde PRODUCTO.md)
// =====================================================================

// Mockup de la caja de 6 volymer (Higgsfield, WebP transparente) – ver teaterklar/media/MOCKUP.md
export const PRODUCT_IMAGE = 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/0d761a1a-51e3-4f5e-b9c4-63a41fd67907.webp';
export const PRODUCT_IMAGE_ALT = 'TEATERKLAR-paketet: sex volymer för drama och teater med årsplanering, lektioner minut för minut, dramaövningar, improvisation, produktionsplan och bedömning';

// ---------- Prices (Market «Sverige») ----------
export const PRICE = '299 kr';
export const ORIGINAL_PRICE = '601 kr';

// ---------- Meta ----------
export const META = {
  title: 'TEATERKLAR™ – Färdiga lektioner i drama och teater',
  description: 'Färdigt 6-i-1-system för drama och teater i grundskolan och gymnasiet: 6 volymer, 240 sidor, 12 lektioner minut för minut, 200 uppvärmningar och dramaövningar, 60 improvisationsövningar, produktionsplan och bedömning E/C/A + 5 bonusar. 299 kr.',
  ogTitle: 'TEATERKLAR™ – Gå in i dramasalen med allt klart',
  ogDescription: 'Hela läsåret i drama och teater – färdigplanerat. Lektioner, övningar, improvisation, föreställning och bedömning för grundskolan och gymnasiet. Omedelbar nedladdning.'
};

// ---------- Top bar (honest: no countdown) ----------
export const topBar = {
  long: 'Engångsbetalning · Omedelbar nedladdning · 7 dagars nöjdhetsgaranti',
  short: 'Engångsbetalning · 7 dagars garanti'
};

// ---------- Section copy ----------
export const copy = {
  ctaDefault: 'Skaffa TEATERKLAR™ nu',
  hero: {
    before: 'Få',
    highlight: 'hela läsåret',
    middle: 'i drama och teater färdigplanerat – kom igång på',
    accent: '5 minuter',
    sub: 'Gå in i dramasalen med allt klart. Sex digitala volymer för grundskolan och gymnasiet med lektioner minut för minut, 200 uppvärmningar och dramaövningar, improvisation, en färdig väg från manus till föreställning och bedömning E/C/A – plus fem bonusar.',
    badgeLine1: '6 volymer',
    badgeLine2: '+ 5 bonusar'
  },
  stats: { title: 'Det kompletta paketet i siffror' },
  difference: {
    kicker: 'Skillnaden med TEATERKLAR™',
    titleStart: 'Från stressad dramalärare till',
    titleAccent: 'trygg och förberedd.',
    intro: 'Tänk dig att börja veckan med dramalektionerna redan organiserade. Du vet vad gruppen ska göra – och du har uppvärmningen, övningarna, scenerna, produktionsplanen och bedömningen klara.',
    beforeLabel: 'Innan TEATERKLAR™',
    beforeTitle: 'Känner du igen dig?',
    afterLabel: 'Med TEATERKLAR™',
    afterTitle: 'Planera mindre. Spela mer.',
    closing: 'Fokusera på dina elever och på scenen – inte på att skapa allt material själv.'
  },
  recognize: {
    title: 'Har du någonsin upplevt det här?',
    text: 'Om du svarar ja på minst en fråga är TEATERKLAR™ för dig.',
    link: 'Du förtjänar ett enklare sätt'
  },
  inside: {
    title: 'Vad innehåller',
    brand: 'TEATERKLAR™',
    packTitle: 'Vad gör TEATERKLAR™ annorlunda?',
    packText: 'Ett helt läsårs material i drama och teater – organiserat och redo att använda.'
  },
  pages: {
    kicker: 'Titta inuti TEATERKLAR™',
    titleStart: 'Se hur materialet ser ut',
    titleAccent: 'på insidan.',
    text: 'Exempel på sidor ur Volym 1: lärarsidor minut för minut, elevblad i tre nivåer, årsplanering och bedömning – skapade för att användas i dramasalen.'
  },
  bonuses: {
    kicker: 'Men det finns mer!',
    titleStart: '5 bonusar',
    titleAccent: 'utan extra kostnad',
    text: 'Fem extra resurser ingår tillsammans med dina sex volymer – så att du kan planera, genomföra och visa upp dramaarbetet ännu enklare.'
  },
  how: {
    titleStart: 'Så fungerar',
    brand: 'TEATERKLAR™',
    text: 'Från nedladdning till dramasalen: förbered nästa lektion på bara fem minuter.',
    closing: 'Öppna → välj → förbered → spela. Enkelt och strukturerat.'
  },
  audience: {
    titleStart: 'För vem är',
    brand: 'TEATERKLAR™',
    lead: 'Paketet är för dig som:',
    closing: 'Skapat för lärare som vill lägga mindre tid på förberedelser och mer tid på eleverna och scenen.',
    asideTitle: 'Vad är din tid värd?',
    asideLead: 'Tänk på hur många timmar du lägger på dramaplanering varje vecka:',
    asideText: 'Tänk dig nu ett organiserat paket som hjälper dig med allt detta – under hela läsåret.',
    asideClosing: 'Du behöver inte fortsätta börja från noll.'
  },
  offer: {
    titleStart: 'Upptäck ditt kompletta',
    brand: 'TEATERKLAR™',
    titleEnd: '-paket',
    text: 'Hela läsåret i drama och teater – färdigplanerat. Kom igång på 5 minuter.',
    includesLabel: 'Det kompletta paketet innehåller',
    priceLabel: 'Introduktionspris',
    originalLabel: 'Ord. pris',
    oneTime: 'Engångsbetalning. Ingen prenumeration.',
    cta: 'Skaffa hela paketet nu'
  },
  guarantee: {
    title: '7 dagars nöjdhetsgaranti',
    text: 'Ladda ner materialet och se hur det fungerar i din drama- och teaterundervisning. Om paketet inte motsvarar dina förväntningar kan du kontakta oss via e-post och begära återbetalning inom 7 dagar från köpet, enligt våra garantivillkor.',
    legal: 'Garantin påverkar inte dina rättigheter enligt gällande konsumentlagstiftning.'
  },
  finalCta: {
    titleStart: 'Din dramaundervisning kan vara',
    titleAccent: 'redo redan idag.',
    text: 'I stället för att leta efter övningar, skriva scener och planera varje lektion själv på kvällarna har du ett organiserat paket för hela läsåret – från första namnleken till föreställningen. Mer tid till det som verkligen betyder något:',
    closing: 'Dina elever. Scenen. Ditt liv utanför skolan.',
    recap: ['6 volymer', '5 bonusar', '240 sidor', 'Grundskolan och gymnasiet'],
    oneTime: 'Engångsbetalning – ingen prenumeration.',
    reassurances: ['Omedelbar digital nedladdning', 'Utskrivbara PDF-filer', '7 dagars nöjdhetsgaranti']
  },
  footer: {
    brand: 'TEATERKLAR',
    disclaimer: 'TEATERKLAR™ är ett fristående undervisningsmaterial i drama och teater för grundskolan och gymnasiet. Materialet bygger på Gy25 och Lgr22. Det är inte granskat eller godkänt av Skolverket.',
    copyright: '© 2026 TEATERKLAR™ · edulabs. Alla rättigheter förbehållna.'
  }
};

// ---------- Data ----------
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];

export const stats = [{
  value: '240',
  label: 'Sidor i sex volymer'
}, {
  value: '12',
  label: 'Lektioner minut för minut'
}, {
  value: '200',
  label: 'Uppvärmningar och dramaövningar'
}, {
  value: '60',
  label: 'Improvisationsövningar'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}];

export const differenceStats = [{
  value: 'Åk 1–9 + gymnasiet',
  label: 'Årsplanering för åk 1–3, 4–6, 7–9 och gymnasiet'
}, {
  value: '6 digitala volymer',
  label: 'Planering, övningar, improvisation, föreställning, dramapedagogik och bedömning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut till dramasalen'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällen försvinner',
  text: 'Timmar framför datorn för att planera veckans dramalektioner – och du är fortfarande inte klar.'
}, {
  title: 'Blandade grupper',
  text: 'Några elever vill stå på scen hela tiden, andra har aldrig spelat teater. Samma övning ska fungera för alla.'
}, {
  title: 'Blyga elever som inte vågar',
  text: 'Du vill att alla ska känna sig trygga – men det är svårt att hitta övningar som bygger trygghet steg för steg.'
}, {
  title: 'Skolavslutningen närmar sig',
  text: 'Pjäs, roller, repetitioner, kostym och programblad – och allt ska vara klart till ett visst datum.'
}, {
  title: 'Leta övningar överallt',
  text: 'Böcker, gamla anteckningar och engelska webbsidor – det tar tid att hitta övningar som passar din grupp.'
}, {
  title: 'Bedömning av något som syns på scen',
  text: 'Att sätta ord på elevernas utveckling i drama och teater tar tid – och kriterierna måste vara tydliga.'
}];

export const afterItems = ['Ett organiserat paket för drama och teater', 'Årsplanering för grundskolan och gymnasiet', '12 lektioner planerade minut för minut', 'Elevblad i 3 nivåer som kan skrivas ut direkt', '200 uppvärmningar och dramaövningar', '60 improvisationsövningar', '8 veckors produktionsplan från manus till föreställning', '6 korta originalpjäser och scener', 'Bedömningsmatriser E/C/A, loggbok och 100 färdiga kommentarer'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagskvällen går åt till planering',
  text: 'Sitter du på söndagskvällen och planerar veckans dramalektioner?'
}, {
  icon: ShuffleIcon,
  title: 'Grupperna är blandade',
  text: 'Behöver du övningar som fungerar både för vana scenelever och för dem som aldrig har spelat teater?'
}, {
  icon: UsersIcon,
  title: 'Blyga elever',
  text: 'Vill du få med de blyga eleverna utan att någon känner sig utpekad?'
}, {
  icon: PartyPopperIcon,
  title: 'Skolavslutningen närmar sig',
  text: 'Ska du sätta upp något till skolavslutningen, luciatåget eller julspelet – och vet inte var du ska börja?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar efter övningar',
  text: 'Söker du ständigt efter uppvärmningar, improvisationer och scener som passar just din grupp?'
}, {
  icon: LayersIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha en röd tråd genom läsåret – från ensemblebygge till föreställning och bedömning?'
}];

// Spine colours = TEATERKLAR palette (marinblå, teaterröd, guld, laxrosa, lila, marinblå) as on the box mockup.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Planering',
  title: 'Årsplanering + 12 lektioner minut för minut',
  tagline: 'Gå in i dramasalen med allt klart.',
  intro: 'Den första volymen ger dig strukturen för hela läsåret och tolv färdiga lektioner på 60 minuter – med lärarsida och elevblad i tre nivåer till varje lektion.',
  items: ['Årsplanering för grundskolan (åk 1–3, 4–6, 7–9) och gymnasiet', 'Terminsplanering vecka för vecka', '12-veckorsplan och progressionstabell', '12 lektioner minut för minut (60 min)', 'Lärarsida + elevblad i 3 nivåer', 'Reflektionsfrågor', 'Bedömningsmatris'],
  outro: 'Från ”Gruppen blir en ensemble” till ”Visning och feedback – vi spelar för varandra” – färdigt att undervisa.',
  spine: 'bg-[#1B2A5E] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Övningar',
  title: '200 uppvärmningar och dramaövningar',
  tagline: 'Rätt övning för varje grupp och varje dag.',
  intro: 'Den andra volymen samlar 200 uppvärmningar och dramaövningar – var och en med tid, ålder, gruppstorlek, steg och variation.',
  items: ['Namnlekar', 'Koncentration och fokus', 'Tillit och samarbete', 'Kropp och rörelse', 'Röst och andning', 'Rytm', 'Gruppbygge', 'Avslutningsövningar'],
  outro: 'Slå upp, välj och sätt igång – utan att leta.',
  spine: 'bg-[#C8323C] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 03',
  level: 'Improvisation',
  title: 'Improvisation och scenisk gestaltning',
  tagline: 'Från ”Ja, och…” till karaktär och scen.',
  intro: 'Den tredje volymen innehåller 60 improvisationsövningar och färdigt material för scenisk gestaltning i tre nivåer.',
  items: ['60 improvisationsövningar', 'Statusarbete', 'Karaktärsarbete', 'Rörelse och mim', 'Röst och diktion', 'Scenisk närvaro', 'Att arbeta med text', 'Monolog och dialog', 'Scenövningar i 3 nivåer'],
  outro: 'Eleverna växer på scen – steg för steg.',
  spine: 'bg-[#F2B134] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 04',
  level: 'Föreställning',
  title: 'Från manus till föreställning',
  tagline: 'Hela produktionen – vecka för vecka.',
  intro: 'Den fjärde volymen tar dig från val av pjäs till föreställningsdagen med en färdig produktionsplan på 8 veckor och 6 korta originalpjäser och scener.',
  items: ['8 veckors produktionsplan', 'Välja pjäs', 'Rollfördelning utan drama', 'Repetitionsschema', 'Regi', 'Scenografi, ljus och ljud', 'Kostym och rekvisita', 'Programblad', 'Föreställningsdagen och checklistor', '6 korta originalpjäser/scener – även för skolavslutning, luciatåg och julspel'],
  outro: 'Föreställningen blir klar i tid – utan kaos.',
  spine: 'bg-[#FF8A7A] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Dramapedagogik',
  title: 'Dramapedagogik i alla ämnen',
  tagline: 'Drama som arbetsform – i hela skolan.',
  intro: 'Den femte volymen visar hur du använder drama i andra ämnen och för trygghet i gruppen – med anpassningar för alla elever.',
  items: ['Drama i svenska, SO, NO, engelska och matematik', 'Forumteater', 'Processdrama', 'Heta stolen', 'Tablå', 'Rollspel', 'Värderingsövningar', 'Drama för trygghet och likabehandling', 'Anpassningar för blyga elever och NPF'],
  outro: 'Drama som gör lärandet levande i alla ämnen.',
  spine: 'bg-[#7B4FA0] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 06',
  level: 'Bedömning',
  title: 'Bedömning',
  tagline: 'Gör bedömningen enklare och tydligare.',
  intro: 'Den sjätte volymen ger dig färdigt stöd för bedömning och omdömen i drama och teater – för grundskolan och gymnasiet.',
  items: ['Bedömningsmatriser E/C/A för gymnasiet och grundskolan', 'Loggbok', 'Självbedömning', 'Kamratrespons', 'Observationsschema', 'Skriftliga reflektionsuppgifter A/B med bedömningsstöd', '100 färdiga kommentarer till omdömen och IUP'],
  outro: 'Mindre tid på dokumentation – tydligare uppföljning.',
  spine: 'bg-[#1B2A5E] text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitala volymer för drama och teater',
  text: 'Ett komplett paket för grundskolan och gymnasiet som hjälper dig att planera och genomföra drama- och teaterundervisningen under hela läsåret. Varje volym innehåller 40 sidor med lektioner, övningar, scener och bedömningsmaterial.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lektioner minut för minut',
  text: 'Slipp börja från ett tomt dokument. 12 lektioner på 60 minuter med lärarsida och elevblad i 3 nivåer.'
}, {
  icon: SparklesIcon,
  title: '200 uppvärmningar och övningar',
  text: 'Namnlekar, fokus, tillit, kropp, röst och rytm – med tid, ålder, gruppstorlek och variation.'
}, {
  icon: ZapIcon,
  title: '60 improvisationsövningar',
  text: 'Från ”Ja, och…” och status till karaktär, dialog och färdiga scenövningar i 3 nivåer.'
}, {
  icon: DramaIcon,
  title: 'Från manus till föreställning',
  text: '8 veckors produktionsplan, checklistor och 6 korta originalpjäser och scener att spela.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömning E/C/A',
  text: 'Bedömningsmatriser, loggbok, kamratrespons och 100 färdiga kommentarer till omdömen och IUP.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Årsplanering för grundskolan och gymnasiet, planering vecka för vecka och progressionstabell.'
}];

// «Titta inuti»: 5 páginas reales del Volym 1 Estudio (s01, s05, s14, s27, s37), WebP en Higgsfield.
export const pageSamples: Array<{ src: string; label: string; title: string; text: string }> = [
  { src: 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/5540ed43-2c09-4d15-add4-2f8ede664c44.webp', label: 'Volym 1 · Omslag', title: 'Årsplanering + 12 lektioner minut för minut', text: 'För åk 1–3, 4–6, 7–9 och gymnasiet – improvisation, kropp och röst, visning.' },
  { src: 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/a98ce22d-d957-4bba-9090-824664346588.webp', label: 'Årsplanering', title: 'Hela läsåret planerat', text: 'Terminen vecka för vecka, så att du alltid vet vad nästa dramalektion handlar om.' },
  { src: 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/6cf4176d-1ccd-4886-86eb-d8f597ed5437.webp', label: 'Lektion minut för minut', title: 'Lektion 2 · Kroppen berättar – frysta bilder', text: 'Mål, material, uppvärmning, huvuddel, visning och reflektion – med lärarens instruktion.' },
  { src: 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/3fd86c7c-2025-497d-97ae-522a81f46450.webp', label: 'Elevblad i 3 nivåer', title: 'Den öppna scenen', text: 'Samma manus, tre nivåer – med lärartips.' },
  { src: 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/afc13db8-c372-416f-b750-1dff3dfbbb28.webp', label: 'Bedömning', title: 'Bedömningsmatris E/C/A', text: 'Ensemble, kropp och röst, improvisation, gestaltning och reflektion.' },
];
export const SHOW_INSIDE_PAGES = pageSamples.length > 0 && pageSamples.every(page => page.src !== '');

export const pageTypes = ['Lektioner minut för minut', 'Elevblad i 3 nivåer', 'Årsplanering', 'Bedömning'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Skolavslutning och lucia – klart',
  text: '3 färdiga föreställningar: skolavslutning, luciatåg och julspel.',
  items: ['Manus', 'Rollista', 'Scenschema', 'Repetitionsplan'],
  benefit: 'Årets största föreställningar – klara att repetera.'
}, {
  title: 'Vikariepärm för drama',
  text: '15 lektioner som vikarien kan genomföra utan förberedelse.',
  items: ['15 färdiga lektioner', 'Instruktion', 'Övningar', 'Reflektionsblad'],
  benefit: 'Lugn och ro även när du inte kan vara på plats.'
}, {
  title: 'Föräldrakommunikation för drama och teater',
  text: 'Färdiga brev till vårdnadshavare under hela läsåret.',
  items: ['Inbjudan till föreställning', 'Rollfördelning', 'Kostym hemifrån', 'Fototillstånd', 'Veckobrev', 'Utvecklingssamtal'],
  benefit: 'Tydlig kommunikation på några minuter.'
}, {
  title: 'Affischer för dramasalen',
  text: '20 affischer som gör dramasalen till en lärmiljö.',
  items: ['Scenens delar', 'Statusskalan', 'Improvisationens regler (”Ja, och…”)', 'Känsloskala', 'Scenord', 'Publikregler', 'Kamratrespons'],
  benefit: 'Skriv ut och sätt upp – direkt.'
}, {
  title: 'Scen- och monologbanken',
  text: '30 korta originalscener och monologer för 1–4 elever.',
  items: ['30 originalscener och monologer', 'För 1–4 elever', 'Nivå för varje text', 'Regitips'],
  benefit: 'Alltid en scen redo – för varje elev och varje grupp.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna TEATERKLAR™',
  text: 'Ladda ner paketet och öppna volymen du behöver – planering, övningar, improvisation, föreställning, dramapedagogik eller bedömning.'
}, {
  title: 'Välj lektion och övningar',
  text: 'Hitta rätt lektion, uppvärmning, improvisation eller scen för din grupp och nivå.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom lärarsidan och skriv ut det material du behöver.'
}, {
  title: 'Gå in i dramasalen',
  text: 'Använd materialet utskrivet eller digitalt – och led lektionen med en tydlig plan.'
}];

export const audienceItems = ['Undervisar i drama eller teater i grundskolan', 'Undervisar på estetiska programmet, inriktning teater', 'Arbetar på kulturskolan', 'Använder drama som arbetsform i andra ämnen', 'Ska sätta upp något till skolavslutning, luciatåg eller julspel', 'Vill ha övningar som fungerar i blandade grupper', 'Vill få med blyga elever på ett tryggt sätt', 'Vill ha färdiga bedömningsmatriser och kommentarer'];

export const timeValueTasks = ['Leta efter uppvärmningar och övningar', 'Skriva scener och manus', 'Planera repetitioner inför föreställningen', 'Anpassa övningar för blandade grupper', 'Dokumentera och skriva omdömen'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Årsplanering, övningar, improvisation, föreställning, dramapedagogik och bedömning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Skolavslutning och lucia, vikariepärm, föräldrakommunikation, affischer och scen- och monologbanken'
}, {
  label: 'För grundskolan och gymnasiet',
  detail: '12 lektioner minut för minut, 200 övningar, 60 improvisationer, produktionsplan och bedömning E/C/A'
}];

export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbart material', 'Ingen fysisk produkt skickas'];

// No buyers yet: only add authentic reviews. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'TEATERKLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de sidor du behöver.'
}, {
  q: 'För vem passar TEATERKLAR™?',
  a: 'För lärare som undervisar i drama och teater: på gymnasiets estetiska program med inriktning teater, på kulturskolan och i grundskolan (åk 1–9), där drama används i undervisningen eller inför skolavslutning, luciatåg och julspel.'
}, {
  q: 'Är detta godkänt av Skolverket?',
  a: 'Nej. TEATERKLAR™ är ett fristående undervisningsmaterial och är inte granskat eller godkänt av Skolverket. Materialet bygger på Gy25 (scenisk gestaltning, sceniskt karaktärsarbete och teaterteori) och Lgr22 (drama som arbetsform och estetiska uttrycksformer).'
}, {
  q: 'Fungerar det i blandade grupper?',
  a: 'Ja. Lektionerna har elevblad i tre nivåer, och varje övning anger tid, ålder, gruppstorlek och variation – så att du kan anpassa efter gruppen du har framför dig.'
}, {
  q: 'Hur får jag med blyga elever?',
  a: 'Övningarna bygger trygghet steg för steg – från namnlekar och samarbete i grupp till scenarbete. Volym 5 innehåller dessutom drama för trygghet och likabehandling och anpassningar för blyga elever och elever med NPF.'
}, {
  q: 'Ingår pjäser och scener?',
  a: 'Ja. Volym 4 innehåller 6 korta originalpjäser och scener för olika åldrar, bonusen ”Skolavslutning och lucia – klart” innehåller 3 färdiga föreställningar och Scen- och monologbanken innehåller 30 korta originalscener och monologer för 1–4 elever. Alla texter är skrivna för TEATERKLAR™.'
}, {
  q: 'Hur fungerar bedömningen?',
  a: 'Volym 6 innehåller bedömningsmatriser E/C/A för gymnasiet och grundskolan, loggbok, självbedömning, kamratrespons, observationsschema och 100 färdiga kommentarer till omdömen och IUP.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'TEATERKLAR™ är utformat för att göra förberedelserna snabbare. Du kan öppna en volym, välja en färdig lektion eller övning och gå igenom lärarsidan på ungefär fem minuter. Tiden kan variera beroende på lektion och grupp.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'TEATERKLAR™ innehåller årsplanering och material som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din planering och elevernas behov.'
}, {
  q: 'Är filerna utskrivbara?',
  a: 'Ja. Allt levereras som utskrivbara PDF-filer. Du väljer själv vilka sidor du skriver ut, och du kan även använda materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. TEATERKLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom 7 dagar från köpet enligt villkoren.'
}];

export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
